import { Stack, Text } from '@mantine/core'
import { ContextModalProps } from '@mantine/modals'
import Big from 'big.js'
import Loader from 'common/Loader'
import AddToExistingOrder from 'components/shortage/AddToExistingOrder'
import GroupedShortageInfo from 'components/shortage/GroupedShortageInfo'
import OrderShortagesForm from 'components/shortage/OrderShortagesForm'
import ThereIsExistingOrder from 'components/shortage/ThereIsExistingOrder'
import { useShortageOrder } from 'hooks/shortage'
import { useEffect, useMemo, useState } from 'react'
import { GroupedShortageTableItem, ShortageOrder } from 'types/shortage'

type Props = {
  groupedShortage: GroupedShortageTableItem
}

enum ModalContentType {
  CREATE_NEW_ORDER,
  THERE_IS_EXISTING_ORDER,
  ADD_TO_EXISTING_ORDER,
  UPDATE_AND_ADD_TO_EXISTING_ORDER,
}

export default function OrderShortagesModal({
  context,
  id,
  innerProps: { groupedShortage },
}: ContextModalProps<Props>) {
  const [modalContent, setModalContent] = useState<ModalContentType>(ModalContentType.CREATE_NEW_ORDER)

  const totalShortagesAmount = useMemo(
    () => groupedShortage.subRows.reduce((sum, shortage) => sum.plus(shortage.amount), new Big(0)).toNumber(),
    [groupedShortage.subRows]
  )

  const existingShortageOrders = useShortageOrder({
    product: groupedShortage.product,
    feature: groupedShortage.feature,
    size: groupedShortage.size,
    category: groupedShortage.category,
    neededAmount: totalShortagesAmount,
  })

  useEffect(() => {
    if (existingShortageOrders.data && existingShortageOrders.data.length > 0) {
      setModalContent(ModalContentType.THERE_IS_EXISTING_ORDER)
    }
  }, [existingShortageOrders.data])

  if (existingShortageOrders.isPending) {
    return <Loader />
  } else if (existingShortageOrders.isError) {
    return <Text c="red">Wystąpił błąd podczas ładowania danych o zamówieniu braków.</Text>
  } else {
    return (
      <ModalContent
        modalContentType={modalContent}
        groupedShortage={groupedShortage}
        totalShortagesAmount={totalShortagesAmount}
        existingShortageOrders={existingShortageOrders.data}
        setModalContent={setModalContent}
        onCancel={() => context.closeModal(id)}
      />
    )
  }
}

type ModalContentProps = {
  modalContentType: ModalContentType
  groupedShortage: GroupedShortageTableItem
  totalShortagesAmount: number
  existingShortageOrders: Array<ShortageOrder>
  setModalContent: (type: ModalContentType) => void
  onCancel: () => void
}

function ModalContent({
  modalContentType,
  groupedShortage,
  totalShortagesAmount,
  existingShortageOrders,
  setModalContent,
  onCancel,
}: ModalContentProps) {
  switch (modalContentType) {
    case ModalContentType.CREATE_NEW_ORDER:
      return (
        <Stack>
          <GroupedShortageInfo groupedShortage={groupedShortage} totalShortagesAmount={totalShortagesAmount} />
          <OrderShortagesForm
            groupedShortage={groupedShortage}
            totalShortagesAmount={totalShortagesAmount}
            onCancel={onCancel}
          />
        </Stack>
      )
    case ModalContentType.THERE_IS_EXISTING_ORDER:
      return (
        <ThereIsExistingOrder
          shortages={groupedShortage.subRows}
          existingShortageOrders={existingShortageOrders}
          goToCreateNewOrder={() => setModalContent(ModalContentType.CREATE_NEW_ORDER)}
          goToAddToExistingOrder={() => setModalContent(ModalContentType.ADD_TO_EXISTING_ORDER)}
        />
      )
    case ModalContentType.ADD_TO_EXISTING_ORDER:
      return (
        <AddToExistingOrder
          shortages={groupedShortage.subRows}
          shortageOrders={existingShortageOrders}
          onCancel={onCancel}
        />
      )
    case ModalContentType.UPDATE_AND_ADD_TO_EXISTING_ORDER:
      return <Text>Zaktualizuj i dodaj do istniejącego zamówienia</Text>
    default:
      return null
  }
}
